import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import Card from './Card';
import NotificationCenter from './NotificationCenter';
import useProject from '../hooks/useProject';
import { getCurrentUser } from '../api';
import { getProjectNavigation, getActiveProjectNavKey } from '../constants/projectNavigation';
import {
  LayoutDashboard,
  Search,
  Gauge,
  TrendingUp,
  AlertTriangle,
  Settings,
  FileText,
  FolderKanban,
  Users,
  Link2,
  UserCog,
  Eye,
  Star,
} from 'lucide-react';

export default function Sidebar({ active = '', onNavigate, user: userProp }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(userProp || null);

  // Initialisation depuis localStorage
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  // Charger les informations utilisateur si non fournies
  useEffect(() => {
    if (!userProp) {
      const fetchUser = async () => {
        try {
          const userData = await getCurrentUser();
          setUser(userData);
        } catch (err) {
          console.warn('Impossible de récupérer les informations utilisateur:', err);
        }
      };
      fetchUser();
    } else {
      setUser(userProp);
    }
  }, [userProp]);

  // Récupérer le projet si on est dans un projet
  const projectSlug = useMemo(() => {
    const match = location.pathname.match(/^\/projects\/([^\/]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  const { project } = useProject(projectSlug);

  // Sauvegarde à chaque changement
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', collapsed);
  }, [collapsed]);


  // Détecter si on est dans un projet
  const isInProject = useMemo(() => {
    return projectSlug &&
      location.pathname.startsWith('/projects/') &&
      !location.pathname.includes('/create') &&
      !location.pathname.includes('/audit') &&
      location.pathname !== '/projects';
  }, [location.pathname, projectSlug]);

  const activeProjectTab = useMemo(() => {
    if (!isInProject || !projectSlug) return '';
    return getActiveProjectNavKey(location.pathname, projectSlug);
  }, [location.pathname, projectSlug, isInProject]);

  const projectTabs = useMemo(() => {
    if (!projectSlug) return [];
    return getProjectNavigation(projectSlug, {
      synthese: LayoutDashboard,
      seo: Search,
      performance: Gauge,
      position: TrendingUp,
      'reference-pages': Star,
      edit: Settings,
    });
  }, [projectSlug]);
  return (
    <aside className={`flex flex-col h-screen border-r border-white/70 shadow-sm sticky top-0 bg-white/60 backdrop-blur-md rounded-tr-lg rounded-br-lg transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'} relative`}>
      <div className={`flex ${collapsed ? 'flex-col items-center gap-3' : 'items-center justify-between'} px-4 py-5 mb-4`}>
        <div className={`flex items-end gap-2 ${collapsed ? '' : 'flex-1 min-w-0'}`}>
          <Logo className="flex-shrink-0" width={collapsed ? 28 : 60} height={collapsed ? 19 : 41} />
          {!collapsed && <span className="font-heading text-xl text-accent font-bold tracking-tight whitespace-nowrap">etrics</span>}
        </div>
        {/* Bouton ouverture/fermeture minimaliste */}
        <button
          className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-transparent text-accent opacity-60 hover:opacity-100 hover:bg-accent/10 transition-all focus:outline-none focus:ring-2 focus:ring-accent/40"
          onClick={() => setCollapsed(c => !c)}
          aria-label={collapsed ? 'Ouvrir la sidebar' : 'Réduire la sidebar'}
        >
          {collapsed ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /><path strokeLinecap="round" strokeLinejoin="round" d="M5 5l7 7-7 7" /></svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /><path strokeLinecap="round" strokeLinejoin="round" d="M19 19l-7-7 7-7" /></svg>
          )}
        </button>
      </div>
      <nav className={`flex-1 px-2 space-y-1 overflow-y-auto`}>
        {/* Navigation principale */}
        <button
          className={`group flex items-center w-full px-2 py-2 rounded-lg transition font-medium text-blue-gray-700 hover:bg-accent/10 ${active === 'dashboard' ? 'bg-accent/10 text-accent' : ''} ${collapsed ? 'justify-center' : ''} focus:outline-none focus:border-none`}
          onClick={() => onNavigate('dashboard')}
        >
          <LayoutDashboard className="w-5 h-5 text-accent flex-shrink-0" strokeWidth={1.7} />
          {!collapsed && <span className="ml-3">Dashboard</span>}
        </button>
        {/* Audits complets : tous les utilisateurs */}
        <button
          className={`group flex items-center w-full px-2 py-2 rounded-lg transition font-medium text-blue-gray-700 hover:bg-accent/10 ${active === 'projects' ? 'bg-accent/10 text-accent' : ''} ${collapsed ? 'justify-center' : ''} focus:outline-none focus:border-none`}
          onClick={() => onNavigate('projects')}
        >
          <FolderKanban className="w-5 h-5 text-accent flex-shrink-0" strokeWidth={1.7} />
          {!collapsed && <span className="ml-3">Audits complets</span>}
        </button>
        {/* Documentation : non commerciaux / non growth marketing */}
        {(!user || (user?.user_type !== 'commercial' && user?.user_type !== 'growth_marketing')) && (
          <button
            className={`group flex items-center w-full px-2 py-2 rounded-lg transition font-medium text-blue-gray-700 hover:bg-accent/10 ${active === 'docs' ? 'bg-accent/10 text-accent' : ''} ${collapsed ? 'justify-center' : ''} focus:outline-none focus:border-none`}
            onClick={() => onNavigate('docs')}
          >
            <FileText className="w-5 h-5 text-accent flex-shrink-0" strokeWidth={1.7} />
            {!collapsed && <span className="ml-3">Documentation</span>}
          </button>
        )}
        {/* Prospects : admin + commercial */}
        {(user?.user_type === 'admin' || user?.user_type === 'commercial') && (
          <button
            className={`group flex items-center w-full px-2 py-2 rounded-lg transition font-medium text-blue-gray-700 hover:bg-accent/10 ${active === 'prospects' ? 'bg-accent/10 text-accent' : ''} ${collapsed ? 'justify-center' : ''} focus:outline-none focus:border-none`}
            onClick={() => onNavigate('prospects')}
          >
            <Users className="w-5 h-5 text-accent flex-shrink-0" strokeWidth={1.7} />
            {!collapsed && <span className="ml-3">Prospects</span>}
          </button>
        )}
        {/* Utilisateurs : admin uniquement */}
        {user?.user_type === 'admin' && (
          <button
            className={`group flex items-center w-full px-2 py-2 rounded-lg transition font-medium text-blue-gray-700 hover:bg-accent/10 ${active === 'admin-users' ? 'bg-accent/10 text-accent' : ''} ${collapsed ? 'justify-center' : ''} focus:outline-none focus:border-none`}
            onClick={() => onNavigate('admin-users')}
          >
            <UserCog className="w-5 h-5 text-accent flex-shrink-0" strokeWidth={1.7} />
            {!collapsed && <span className="ml-3">Utilisateurs</span>}
          </button>
        )}

        {/* Section Navigation du projet */}
        {isInProject && (
          <div className={`${collapsed ? 'px-0' : 'w-full'}`}>
            {collapsed ? (
              <div className="w-full h-px bg-blue-gray-200 my-2"></div>
            ) : (
              <Card className="p-4 bg-white/80 border-blue-gray-200/60 overflow-visible">
                <div className="mb-2.5 pb-2.5 border-b border-blue-gray-200">
                  <div className="text-[10px] font-heading font-semibold text-blue-gray-500 uppercase tracking-wider mb-1.5">
                    Navigation
                  </div>
                  <div className="text-[11px] text-blue-gray-600 font-medium truncate px-2 py-1 bg-blue-gray-50/80 rounded border border-blue-gray-200/50">
                    {projectSlug}
                  </div>
                </div>
                <div className="space-y-0.5 -mx-1 max-h-[min(52vh,360px)] overflow-y-auto overscroll-contain pr-0.5 custom-scrollbar">
                  {projectTabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeProjectTab === tab.key;
                    return (
                      <button
                        key={tab.key}
                        className={`group relative flex items-center w-full px-3 py-1.5 rounded-r-md transition-all duration-150 font-medium text-xs ${isActive
                            ? 'text-accent bg-accent/10'
                            : 'text-blue-gray-600 hover:bg-blue-gray-50/80 hover:text-blue-gray-900'
                          } focus:outline-none focus:ring-2 focus:ring-accent/20`}
                        onClick={() => navigate(tab.path)}
                        title={tab.label}
                      >
                        {isActive && (
                          <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-accent"></div>
                        )}
                        <Icon className={`w-[16px] h-[16px] flex-shrink-0 ${isActive ? 'text-accent ml-0.5' : 'text-blue-gray-500 group-hover:text-blue-gray-700 ml-0.5'}`} strokeWidth={isActive ? 2 : 1.7} />
                        <span className={`ml-2.5 truncate ${isActive ? 'text-accent' : ''}`}>{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </Card>
            )}
            {collapsed && (
              <div className="space-y-1 mt-2">
                {/* Favicon du projet en version réduite */}
                {project?.favicon_url && (
                  <div className="flex items-center justify-center mb-2 px-2">
                    <img
                      src={project.favicon_url}
                      alt={project.name || 'Projet'}
                      className="w-8 h-8 rounded-lg bg-white border border-blue-gray-100 shadow-sm"
                      onError={(e) => {
                        e.target.src = `https://www.google.com/s2/favicons?domain=${project.name || projectSlug}&sz=128`;
                      }}
                    />
                  </div>
                )}
                {projectTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeProjectTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      className={`group relative flex items-center justify-center w-full px-2 py-2 rounded-md transition-all duration-150 font-medium text-xs ${isActive
                          ? 'text-accent bg-accent/10'
                          : 'text-blue-gray-600 hover:bg-blue-gray-50/80'
                        } focus:outline-none focus:ring-2 focus:ring-accent/20`}
                      onClick={() => navigate(tab.path)}
                      title={tab.label}
                    >
                      {isActive && (
                        <div className="absolute left-0 top-1 bottom-1 w-1 bg-accent rounded-r-full"></div>
                      )}
                      <Icon className={`w-[16px] h-[16px] flex-shrink-0 ${isActive ? 'text-accent' : 'text-blue-gray-500 group-hover:text-blue-gray-700'}`} strokeWidth={isActive ? 2 : 1.7} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </nav>
      <div className={`mt-auto px-4 py-5 border-t border-blue-100 flex flex-col gap-3 ${collapsed ? 'items-center py-4' : ''}`}>
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-end'}`}>
          <NotificationCenter />
        </div>
        <button
          className={`group flex items-center gap-3 w-full bg-transparent border-0 p-0 m-0 text-left hover:bg-accent/5 rounded-xl transition ${collapsed ? 'flex-col justify-center py-2' : 'py-3'} focus:outline-none focus:border-none`}
          onClick={() => onNavigate('profile')}
          aria-label="Accéder au profil"
          tabIndex={0}
        >
          <span className="flex-shrink-0">
            {user?.avatar ? (
              <img src={user.avatar} alt="avatar" className="w-10 h-10 rounded-full border border-blue-100 shadow-sm" />
            ) : (
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent/10 text-accent font-bold text-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 4-7 8-7s8 3 8 7" /></svg>
              </span>
            )}
          </span>
          {!collapsed && (
            <span className="flex-1 min-w-0">
              <div className="font-heading text-sm text-blue-gray-900 truncate">{user?.first_name || 'Mon profil'}</div>
              <div className="text-xs text-blue-gray-400 truncate">{user?.email || ''}</div>
            </span>
          )}
        </button>
      </div>
    </aside>
  );
} 